import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, UserPlus } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/** Faults ranked by the money they waste, so crews fix the expensive ones first. */
export function FaultCostRanking({ rows, totalCost, onOpen, onClaimed }) {
  const [claiming, setClaiming] = useState(null);
  const [claimed, setClaimed] = useState([]);

  const ranked = useMemo(
    () => rows.filter((r) => r.cost).sort((a, b) => b.cost - a.cost).slice(0, 8),
    [rows]
  );

  const claim = async (row) => {
    setClaiming(row.rule_id);
    try {
      await api.post("/faults/tasks", {
        site_id: row.site_id, rule_id: row.rule_id, rule: row.rule,
        equip: (row.equips || [])[0] || null, cost: row.cost, hours: row.hours,
      });
      setClaimed((c) => [...c, `${row.site_id}|${row.rule_id}`]);
      toast.success("Claimed — it's on your to-do list");
      onClaimed?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail || e.message));
    } finally {
      setClaiming(null);
    }
  };

  if (ranked.length === 0) return null;
  const top = ranked[0].cost || 1;

  return (
    <div className="mt-6 k-card p-6" data-testid="fault-cost-ranking">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="k-label flex items-center gap-2"><DollarSign className="h-3.5 w-3.5" /> Costliest faults</div>
          <p className="mt-1 k-sublabel">Fix these first — they account for most of the wasted spend this period.</p>
        </div>
        <div className="text-sm text-[#4D4D4D]">
          Ranked impact <span className="font-heading font-bold text-[#212121]">{money(ranked.reduce((s, r) => s + r.cost, 0))}</span>
          {totalCost ? <span className="k-sublabel"> of {money(totalCost)}</span> : null}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {ranked.map((r, i) => (
          <div key={`${r.site_id}-${r.rule_id}`} data-testid={`cost-rank-${i + 1}`}>
            <div className="flex items-center justify-between gap-4">
              <button onClick={() => onOpen?.(r)} className="flex items-center gap-3 min-w-0 text-left group">
                <span className="text-xs font-heading font-bold text-[#9CA3AF] w-4">{i + 1}</span>
                <span className="text-sm font-heading font-semibold text-[#212121] truncate group-hover:text-[#156F5B]">
                  {r.rule}
                </span>
              </button>
              <div className="flex items-center gap-3 shrink-0">
                <Link
                  to={`/sites/${encodeURIComponent(r.site_id)}/energy`}
                  className="k-sublabel hover:text-[#156F5B] hidden sm:inline"
                >
                  {r.site_dis}
                </Link>
                <span className="text-sm font-heading font-semibold text-[#C2410C]">{money(r.cost)}</span>
                <button
                  onClick={() => claim(r)}
                  disabled={claiming === r.rule_id || claimed.includes(`${r.site_id}|${r.rule_id}`)}
                  className="k-btn-outline"
                  data-testid={`claim-fault-${i + 1}`}
                >
                  <UserPlus className="h-3 w-3" />
                  {claimed.includes(`${r.site_id}|${r.rule_id}`) ? "Claimed" : "Claim"}
                </button>
              </div>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.max(4, (r.cost / top) * 100)}%`, background: "#C2410C", transition: "width .4s ease" }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
